import * as angular from 'angular';
import * as _ from 'lodash';
import { IAlarmService } from '../../services';
import { Store, Dispatch, Dispatcher } from '../Store';
import * as Actions from './alarms.actions';
import { AlarmList, Alarm } from '../../models';

export interface IAlarmActionsService {
    getOne(guid: string): Dispatcher<Alarm>;
    getByIdentifierGuid(guid: string): Dispatcher<Alarm[]>;
    getAll(): Dispatcher<AlarmList>;
    getAll(force: boolean): Dispatcher<AlarmList>;
    getAllForIdentifier(identifierGuid: string): Dispatcher<Alarm[]>;
    getAllForIdentifiers(identifierGuids: string[]): Dispatcher<AlarmList>;
    addOne(identifierGuid: string, targetAlarm: Alarm): Dispatcher<AlarmList>;
    addMany(identifierGuid: string, targetAlarms: Alarm[]): Dispatcher<AlarmList>;
    updateOne(identifierGuid: string, targetAlarm: Alarm): Dispatcher<AlarmList>;
    updateMany(identifierGuid: string, targetAlarms: Alarm[]): Dispatcher<AlarmList>;
    updateMultiple(value: AlarmList): Dispatcher<AlarmList>;
    deleteOne(identifierGuid: string, alarmGuid: string): Dispatcher<AlarmList>;
    forceInMemoryUpdate(updated: AlarmList): Dispatcher<void>;
    // deleteMany(guids: string[]): Dispatcher<AlarmList>;
}

export class AlarmActionsService implements IAlarmActionsService {
    static serviceId = 'alarmActionsService';
    static $inject = ['$q', 'alarmService'];

    constructor(
        private _$q: angular.IQService,
        private _alarmService: IAlarmService) {
    }

    getOne(guid: string): Dispatcher<Alarm> {
        return (dispatch: Dispatch<Alarm>, getState: () => Store) => {
            const { alarms } = getState();
            if (!alarms.isLoading && !alarms.isLoaded) {
                dispatch(Actions.loadAlarm());
                return this._alarmService.getAll()
                    .then(data => {
                        dispatch(Actions.loadAlarmSuccess(data) as any);
                        const result = _.find(_.flatten(_.values(data)), d => d.guid === guid);
                        return result ?? this._$q.when({});
                    })
                    .catch((reason) => {
                        dispatch(Actions.loadAlarmError(reason));
                        return reason;
                    });
            } else {
                const results = _.flatten(_.values(alarms.dictionary));
                if (results && results.length > 0) {
                    return this._$q.when(results.filter(r => r.guid === guid));
                } else {
                    return this._$q.when({});
                }
            }
        };
    }

    getByIdentifierGuid(guid: string): Dispatcher<Alarm[]> {
        return (dispatch: Dispatch<Alarm[]>, getState: () => Store) => {
            const { alarms } = getState();
            if (!alarms.isLoading && !alarms.isLoaded) {
                dispatch(Actions.loadAlarms());
                return this._alarmService.getAll()
                    .then(data => {
                        dispatch(Actions.loadAlarmsSuccess(data) as any);
                        return data[guid];
                    })
                    .catch((reason) => {
                        dispatch(Actions.loadAlarmsByIdentifierError(reason));
                        return reason;
                    });
            } else {
                return this._$q.when(alarms.dictionary[guid]);
            }
        };
    }

    getAll();
    getAll(force: boolean);
    getAll(force?: boolean): Dispatcher<AlarmList> {
        return (dispatch: Dispatch<AlarmList>, getState: () => Store) => {
            const { alarms } = getState();
            if (force || !alarms.isLoading && !alarms.isLoaded) {
                dispatch(Actions.loadAlarms());
                return this._alarmService.getAll(force)
                    .then(data => {
                        const results = data;
                        dispatch(Actions.loadAlarmsSuccess(results));
                        return results;
                    })
                    .catch((reason) => {
                        dispatch(Actions.loadAlarmsError(reason));
                        return reason;
                    });
            } else {
                return this._$q.when(alarms.dictionary);
            }
        };
    }

    getAllForIdentifier(IdentifierGuid: string): Dispatcher<Alarm[]> {
        return (dispatch: Dispatch<Alarm[]>, getState: () => Store) => {
            const { alarms } = getState();
            if (!alarms.isLoading && !alarms.isLoaded) {
                dispatch(Actions.loadAlarms());
                return this._alarmService.getAll()
                    .then(data => {
                        dispatch(Actions.loadAlarmsByIdentifierSuccess(data) as any);
                        return data[IdentifierGuid];
                    })
                    .catch((reason) => {
                        dispatch(Actions.loadAlarmsByIdentifierError(reason));
                        return reason;
                    });
            } else {
                return this._$q.when(alarms.dictionary[IdentifierGuid]);
            }
        };
    }

    getAllForIdentifiers(identifierGuids: string[]): Dispatcher<AlarmList> {
        return (dispatch: Dispatch<AlarmList>, getState: () => Store) => {
            const { alarms } = getState();
            if (!alarms.isLoading && !alarms.isLoaded) {
                dispatch(Actions.loadAlarms());
                return this._alarmService.getAll()
                    .then((data: AlarmList) => {
                        const results = {};
                        identifierGuids.forEach(i => {
                            if (data[i]) {
                                results[i] = data[i];
                            } else {
                                results[i] = [];
                            }
                        });
                        dispatch(Actions.loadAlarmsByIdentifierSuccess(data));
                        return results;
                    })
                    .catch((reason) => {
                        dispatch(Actions.loadAlarmsByIdentifierError(reason));
                        return reason;
                    });
            } else {
                const results = {};
                identifierGuids.forEach(i => {
                    if (alarms.dictionary[i]) {
                        results[i] = alarms.dictionary[i];
                    } else {
                        results[i] = [];
                    }
                });
                return this._$q.when(results);
            }
        };
    }

    addOne(identifierGuid: string, targetAlarm: Alarm): Dispatcher<AlarmList> {
        return this.updateMany(identifierGuid, [targetAlarm]);
    }

    addMany(identifierGuid: string, targetAlarms: Alarm[]): Dispatcher<AlarmList> {
        return this.updateMany(identifierGuid, targetAlarms);
    }

    updateOne(identifierGuid: string, targetAlarm: Alarm): Dispatcher<AlarmList> {
        return this.updateMany(identifierGuid, [targetAlarm]);
    }

    updateMany(identifierGuid: string, targetAlarms: Alarm[]): Dispatcher<AlarmList> {
        return (dispatch: Dispatch<AlarmList>, getState: () => Store) => {
            const { alarms } = getState();
            if (identifierGuid && targetAlarms && targetAlarms.length > 0 && alarms.isLoaded) {
                dispatch(Actions.updateAlarms());
                return this._alarmService.save(identifierGuid, targetAlarms)
                    .then(data => {
                        const results = { [identifierGuid]: data };
                        dispatch(Actions.updateAlarmsSuccess(results));
                        const updatedAlarms = alarms.dictionary;
                        updatedAlarms[identifierGuid] = data;
                        dispatch(Actions.AlarmsAfterUpdate(updatedAlarms));
                        return results;
                    })
                    .catch((reason) => {
                        dispatch(Actions.updateAlarmsError(reason));
                        return reason;
                    });
            } else {
                return this._$q.when(alarms.dictionary[identifierGuid]);
            }
        };
    }

    updateMultiple(value: AlarmList): Dispatcher<AlarmList> {
        return (dispatch: Dispatch<AlarmList>, getState: () => Store) => {
            const { alarms } = getState();
            dispatch(Actions.updateAlarms());
            return this._alarmService.saveList(value)
                .then(data => {
                    dispatch(Actions.updateAlarmsSuccess(data));
                    const updatedAlarms = alarms.dictionary;
                    Object.keys(data).forEach(key => {
                        updatedAlarms[key] = data[key];
                    });
                    dispatch(Actions.AlarmsAfterUpdate(updatedAlarms));
                    return data;
                })
                .catch((reason) => {
                    dispatch(Actions.updateAlarmsError(reason));
                    return reason;
                });
        };
    }


    deleteOne(identifierGuid: string, alarmGuid: string): Dispatcher<AlarmList> {
        return (dispatch: Dispatch<AlarmList>, getState: () => Store) => {
            const { alarms } = getState();
            if (identifierGuid && alarmGuid && alarms.isLoaded) {
                dispatch(Actions.deleteAlarm());
                const alarmsForIdentifier = alarms.dictionary[identifierGuid];
                const deletedAlarm = alarmsForIdentifier.filter(a => a.guid === alarmGuid);
                return this._alarmService.delete(identifierGuid, alarmGuid)
                    .then(() => {
                        const results = { [identifierGuid]: deletedAlarm };
                        dispatch(Actions.deleteAlarmSuccess(results));
                        const updatedAlarms = alarms.dictionary;
                        Object.keys(results).forEach(key => {
                            updatedAlarms[key] = updatedAlarms[key].filter(e => results[key].indexOf(e) >= 0);
                        });
                        dispatch(Actions.AlarmsAfterUpdate(updatedAlarms));
                        return results;
                    })
                    .catch((reason) => {
                        dispatch(Actions.deleteAlarmError(reason));
                        return reason;
                    });
            } else {
                return this._$q.when();
            }
        };
    }

    forceInMemoryUpdate(updated: AlarmList): Dispatcher<void> {
        return (dispatch: any, getState: () => Store) => {
            const { alarms } = getState();
            const updatedAlarms = alarms.dictionary;
            Object.keys(updated).forEach(key => {
                updatedAlarms[key] = updated[key];
            });
            dispatch(Actions.AlarmsAfterUpdate(updatedAlarms));
            return this._$q.resolve();
        };
    }

    deleteMany(guids: string[]) {
    }
}